import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { MagnifyingGlass, Receipt, Tray, Command, ArrowRight, Hash } from '@phosphor-icons/react';

export default function QuickSearch({ open, onClose }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ uitgaand: [], inkomend: [] });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const timer = useRef(null);
  const navigate = useNavigate();

  // Flat list for keyboard nav
  const flat = useMemo(() => {
    const out = [];
    results.uitgaand.forEach(d => out.push({ kind: 'uit', doc: d, href: `/documenten/${d.id}/bewerk` }));
    results.inkomend.forEach(d => out.push({ kind: 'ink', doc: d, href: `/inkomende-documenten/${d.id}` }));
    return out;
  }, [results]);

  // Focus on open + reset
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setActiveIdx(0);
    } else {
      setQ(''); setResults({ uitgaand: [], inkomend: [] });
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 2) {
      setResults({ uitgaand: [], inkomend: [] }); setActiveIdx(0); return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await API.get(`/zoek-referentie?q=${encodeURIComponent(q.trim())}`);
        setResults(r.data || { uitgaand: [], inkomend: [] });
        setActiveIdx(0);
      } catch { setResults({ uitgaand: [], inkomend: [] }); }
      setLoading(false);
    }, 200);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q, open]);

  // Keyboard nav: ↑ ↓ Enter Esc
  const onKey = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, flat.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    if (e.key === 'Enter' && flat[activeIdx]) {
      e.preventDefault();
      navigate(flat[activeIdx].href);
      onClose();
    }
  };

  if (!open) return null;

  const fmtEur = (n) => (Number(n) || 0).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR' });
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-';
  const highlight = (text) => {
    if (!q || !text) return text;
    const parts = String(text).split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((p, i) => p.toLowerCase() === q.toLowerCase()
      ? <mark key={i} className="bg-yellow-200 text-gray-900 font-bold px-0.5">{p}</mark>
      : <span key={i}>{p}</span>);
  };

  const totalResults = flat.length;

  const pickItem = (idx) => { navigate(flat[idx].href); onClose(); };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-start justify-center pt-[12vh] px-4" onClick={onClose} data-testid="quicksearch-overlay">
      <div className="bg-white w-full max-w-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} onKeyDown={onKey}>
        {/* Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <MagnifyingGlass size={20} weight="bold" className="text-[#002FA7]" />
          <input ref={inputRef} type="search" value={q} onChange={e => setQ(e.target.value)}
            className="flex-1 outline-none border-0 bg-transparent text-base text-gray-900 placeholder-gray-400"
            placeholder="Zoek op werkbon, factuurnummer, referentie, leverancier..."
            data-testid="quicksearch-input" />
          <kbd className="text-[10px] font-bold text-gray-400 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded">ESC</kbd>
        </div>

        {/* Status */}
        {loading && <div className="px-5 py-3 text-xs text-gray-400 border-b border-gray-100">Zoeken...</div>}
        {!loading && q.trim().length >= 2 && totalResults === 0 && (
          <div className="px-5 py-8 text-center text-sm text-gray-400" data-testid="quicksearch-empty">
            Geen documenten gevonden met <span className="font-mono font-bold text-gray-600">{q}</span>
          </div>
        )}
        {!loading && q.trim().length > 0 && q.trim().length < 2 && (
          <div className="px-5 py-3 text-xs text-gray-400">Typ minstens 2 karakters...</div>
        )}

        {/* Results */}
        {totalResults > 0 && (
          <div className="overflow-y-auto max-h-[55vh]" data-testid="quicksearch-results">
            {/* Uitgaand */}
            {results.uitgaand.length > 0 && (
              <div>
                <div className="px-5 py-1.5 bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-2 sticky top-0">
                  <Receipt size={12} /> Uitgaande facturen
                  <span className="ml-auto text-[10px] font-bold bg-[#002FA7] text-white px-1.5 py-0.5">{results.uitgaand.length}</span>
                </div>
                {results.uitgaand.map((d, i) => {
                  const idx = i;
                  const isActive = activeIdx === idx;
                  return (
                    <button key={d.id} className={`w-full text-left px-5 py-2.5 border-b border-gray-100 flex items-center gap-3 ${isActive ? 'bg-[#F0F4FF]' : 'hover:bg-gray-50'}`} onClick={() => pickItem(idx)} onMouseEnter={() => setActiveIdx(idx)} data-testid={`qs-uit-${d.id}`}>
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 bg-gray-100 text-gray-600 shrink-0">{d.type}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-mono font-bold text-gray-900 truncate">{highlight(d.id)}</span>
                          <span className="text-gray-400 text-xs truncate">· {d.klant}</span>
                        </div>
                        {d.matching_lijnen && d.matching_lijnen.length > 0 && (
                          <div className="text-[11px] text-gray-500 truncate">
                            {d.matching_lijnen.slice(0, 2).map((l, j) => (
                              <span key={j}>
                                {j > 0 && <span className="mx-1">·</span>}
                                <span className="font-mono font-bold text-[#002FA7]">{highlight(l.referentie)}</span>
                                <span className="ml-1">{l.omschr?.substring(0, 45)}</span>
                              </span>
                            ))}
                            {d.matching_lijnen.length > 2 && <span className="ml-1 text-gray-400">+{d.matching_lijnen.length - 2}</span>}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-mono font-bold text-gray-900">{fmtEur(d.totaal)}</div>
                        <div className="text-[10px] text-gray-400">{fmtDate(d.datum)}</div>
                      </div>
                      {isActive && <ArrowRight size={14} className="text-[#002FA7] shrink-0" weight="bold" />}
                    </button>
                  );
                })}
              </div>
            )}
            {/* Inkomend */}
            {results.inkomend.length > 0 && (
              <div>
                <div className="px-5 py-1.5 bg-gray-50 text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-2 sticky top-0">
                  <Tray size={12} /> Inkomende documenten
                  <span className="ml-auto text-[10px] font-bold bg-[#D97706] text-white px-1.5 py-0.5">{results.inkomend.length}</span>
                </div>
                {results.inkomend.map((d, i) => {
                  const idx = results.uitgaand.length + i;
                  const isActive = activeIdx === idx;
                  return (
                    <button key={d.id} className={`w-full text-left px-5 py-2.5 border-b border-gray-100 flex items-center gap-3 ${isActive ? 'bg-amber-50' : 'hover:bg-gray-50'}`} onClick={() => pickItem(idx)} onMouseEnter={() => setActiveIdx(idx)} data-testid={`qs-ink-${d.id}`}>
                      <Tray size={14} className="text-[#D97706] shrink-0" weight="bold" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-mono font-bold text-gray-900 truncate">{highlight(d.factuurnummer || d.id)}</span>
                          <span className="text-gray-400 text-xs truncate">· {highlight(d.leverancier)}</span>
                        </div>
                        {d.matched_fields && d.matched_fields.length > 0 && (
                          <div className="text-[11px] text-gray-500 truncate font-mono">
                            {d.matched_fields[0].veld}: {highlight(d.matched_fields[0].waarde?.substring(0, 60))}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-mono font-bold text-gray-900">{fmtEur(d.totaal)}</div>
                        <div className="text-[10px] text-gray-400">{fmtDate(d.datum)}</div>
                      </div>
                      {isActive && <ArrowRight size={14} className="text-[#D97706] shrink-0" weight="bold" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Footer hints */}
        <div className="flex items-center gap-3 px-5 py-2 bg-gray-50 border-t border-gray-100 text-[10px] text-gray-400">
          <span className="flex items-center gap-1"><kbd className="bg-white border border-gray-200 px-1 py-0.5 font-mono">↑</kbd><kbd className="bg-white border border-gray-200 px-1 py-0.5 font-mono">↓</kbd> navigeren</span>
          <span className="flex items-center gap-1"><kbd className="bg-white border border-gray-200 px-1 py-0.5 font-mono">↵</kbd> openen</span>
          <span className="flex items-center gap-1"><kbd className="bg-white border border-gray-200 px-1 py-0.5 font-mono">ESC</kbd> sluiten</span>
          <span className="ml-auto flex items-center gap-1"><Command size={10} /> + K voor deze zoekbalk</span>
        </div>
      </div>
    </div>
  );
}
